import React from 'react';
import EditParcelModal from '../../../pages/massUpload/components/EditParcelModal';
import {formatPhoneNumber, isPhoneNumber} from '../../../services/phoneFormatter';

/**
 * Компонент ячейки для массовой загрузки с поддержкой редактирования через модалку
 */
const TableCellValueMassUpload = ({column, row, reload, onBeforeOpen, onSave}) => {
    const [modalOpen, setModalOpen] = React.useState(false);
    const mountedRef = React.useRef(true);
    const isEditable = column.canEdit !== false;
    
    React.useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);
    
    let value = row[column.fieldName] != null ? row[column.fieldName] : '';
    
    const normalizeNumericInput = (rawValue) => {
        if (rawValue === null || rawValue === undefined) {
            return '';
        }

        // Поддержка форматов вида "3 000 000", "3 000 000", "3,000,000" и "3 000 000 р"
        return String(rawValue)
            .replace(/\s+/g, '')
            .replace(/\u00A0/g, '')
            .replace(/\u202F/g, '')
            .replace(/[р\u20BD]/gi, '')
            .replace(/,/g, '.')
            .trim();
    };

    // Форматируем числовые значения для веса, объема и количества мест
    const numericFields = ['approximateWeight', 'approximateVolume', 'cargoWeight', 'cargoVolume', 
                          'weight', 'volume', 'packagesCount', 'cargoPackages', 'declaredCargoValue'];
    if (numericFields.includes(column.fieldName)) {
        if (column.fieldName === 'declaredCargoValue' && (value === '' || value === null || value === undefined)) {
            value = '0 р';
        } else if (value !== '' && value !== null && value !== undefined) {
            try {
                // Преобразуем строку в число с очисткой разделителей тысяч
                const numStr = normalizeNumericInput(value);
                const parsedNum = Number.parseFloat(numStr);
                if (!isNaN(parsedNum)) {
                    // Форматируем число с правильным количеством знаков после запятой
                    // Для количества мест - целое число, для объявленной стоимости - формат "N р",
                    // для веса/объема - до 5 знаков
                    let formattedValue;
                    if (column.fieldName === 'packagesCount' || column.fieldName === 'cargoPackages') {
                        formattedValue = parsedNum.toFixed(0);
                        value = formattedValue.replace('.', ',');
                    } else if (column.fieldName === 'declaredCargoValue') {
                        const normalizedRubles = parsedNum
                            .toFixed(2)
                            .replace(/\.?0+$/, '')
                            .replace('.', ',');
                        value = `${normalizedRubles} р`;
                    } else {
                        // Для веса и объема - до 5 знаков, убираем лишние нули в конце
                        formattedValue = parsedNum.toFixed(5).replace(/\.?0+$/, '');
                        value = formattedValue.replace('.', ',');
                    }
                }
            } catch (e) {
                // Оставляем исходное значение при ошибке
            }
        }
    }
    
    // Форматируем дату отгрузки в формате дд.мм.гггг
    if (column.fieldName === 'departureDate' && value) {
        try {
            // Если значение уже в формате dd.MM.yyyy или dd/MM/yyyy, парсим правильно
            if (typeof value === 'string' && /^\d{1,2}[./]\d{1,2}[./]\d{2,4}$/.test(value.trim())) {
                // ВАЖНО: Всегда парсим как dd.MM.yyyy (день.месяц.год), чтобы избежать путаницы с MM/dd
                const {parse} = require('../../../services/dateUtils');
                const dateStr = value.trim();
                
                // Заменяем слеши на точки для единообразия
                const normalizedValue = dateStr.replace(/\//g, '.');
                
                // Пробуем разные форматы с приоритетом dd.MM.yyyy
                let parsedDate = parse(normalizedValue, 'dd.MM.yyyy') || 
                                parse(normalizedValue, 'd.MM.yyyy') ||
                                parse(normalizedValue, 'dd.M.yyyy') ||
                                parse(normalizedValue, 'd.M.yyyy') ||
                                parse(normalizedValue, 'dd.MM.yy') ||
                                parse(normalizedValue, 'd.MM.yy');
                
                // Если парсинг не удался, используем ручной парсинг с явным указанием формата dd.MM.yyyy
                if (!parsedDate || isNaN(parsedDate.getTime())) {
                    const parts = normalizedValue.split('.');
                    if (parts.length === 3) {
                        const day = parseInt(parts[0], 10);
                        const month = parseInt(parts[1], 10);
                        let year = parseInt(parts[2], 10);
                        
                        // Проверяем валидность: день должен быть 1-31, месяц 1-12
                        if (day >= 1 && day <= 31 && month >= 1 && month <= 12) {
                            // Используем полный год (если двузначный, добавляем 2000)
                            const fullYear = year < 100 ? 2000 + year : year;
                            // ВАЖНО: Создаем Date с явным указанием: год, месяц-1, день
                            // Это гарантирует правильную интерпретацию (не MM/dd, а dd/MM)
                            parsedDate = new Date(fullYear, month - 1, day);
                        }
                    }
                }
                
                if (parsedDate && !isNaN(parsedDate.getTime())) {
                    // Форматируем в единый формат dd.MM.yyyy
                    const day = String(parsedDate.getDate()).padStart(2, '0');
                    const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
                    const year = parsedDate.getFullYear();
                    value = `${day}.${month}.${year}`;
                } else {
                    // Если парсинг не удался, пробуем стандартный Date (может быть ISO формат)
                    const date = new Date(value);
                    if (!isNaN(date.getTime())) {
                        const day = String(date.getDate()).padStart(2, '0');
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const year = date.getFullYear();
                        value = `${day}.${month}.${year}`;
                    }
                }
            } else {
                // Если это не строка в формате даты, пробуем парсить как Date
                const date = new Date(value);
                if (!isNaN(date.getTime())) {
                    // Явное форматирование в формат дд.мм.гггг
                    const day = String(date.getDate()).padStart(2, '0');
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const year = date.getFullYear();
                    value = `${day}.${month}.${year}`;
                }
            }
        } catch (e) {
            // Оставляем исходное значение при ошибке
        }
    }
    
    // Форматируем телефонные номера только для полей телефонов
    const phoneFields = ['senderPhone', 'senderAdditionalPhone', 'senderAdditionalPhone2', 
                        'recipientPhone', 'recipientAdditionalPhone', 'recipientAdditionalPhone2'];
    if (value && phoneFields.includes(column.fieldName) && isPhoneNumber(value)) {
        value = formatPhoneNumber(value);
    }
    
    const hasError = row.invalidFields && row.invalidFields.includes(column.fieldName);
    
    const cellStyle = {
        cursor: isEditable ? 'pointer' : 'default',
        padding: '4px 8px',
        borderRadius: '3px',
        display: 'inline-block',
        minWidth: '50px',
        maxWidth: '100%',
        backgroundColor: hasError ? '#fff4f4' : (column.highlighted ? '#fff9db' : 'transparent'),
        border: hasError ? '1px solid #ffcccc' : '1px solid transparent',
        wordWrap: 'break-word',
        overflowWrap: 'break-word'
    };

    const handleClick = () => {
        if (!isEditable) {
            return;
        }

        if (mountedRef.current) {
            // Вызываем callback для фиксации скролла ДО открытия модалки
            onBeforeOpen && onBeforeOpen();
            setModalOpen(true);
        }
    };

    return (
        <span>
            <span 
                style={cellStyle}
                onClick={handleClick}
                title={
                    isEditable
                        ? (hasError ? 'Есть ошибка валидации. Нажмите для редактирования' : 'Нажмите для редактирования')
                        : 'Поле только для просмотра'
                }
            >
                {value || <span style={{color: '#ccc'}}>пусто</span>}
            </span>
            {isEditable && (
                <span>
                    {' '}
                    <a 
                        className='copy-string__icon'
                        onClick={handleClick}
                        style={{cursor: 'pointer'}}
                    >
                        <span className="glyphicon glyphicon-pencil"></span>
                    </a>
                </span>
            )}
            
            {isEditable && (
                <EditParcelModal
                    open={modalOpen}
                    parcel={row._parcel}
                    column={column}
                    onClose={() => {
                        if (mountedRef.current) {
                            setModalOpen(false);
                        }
                    }}
                    onSave={(parcelId, fieldName, value) => {
                        if (mountedRef.current) {
                            setModalOpen(false);
                        }
                        // Вызываем callback для обновления данных и восстановления скролла
                        onSave && onSave(parcelId, fieldName, value);
                    }}
                />
            )}
        </span>
    );
};

export default TableCellValueMassUpload;




// WEBPACK FOOTER //
// ./App/components/Table/components/TableCellValueMassUpload.jsx